import React, { useState } from 'react'
import {
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  TextField,
  Box,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { gql, useMutation } from '@apollo/client'
import { get } from 'lodash'
import DeleteOutlineOutlined from 'components/material-symbol/icons/DeleteOutline'
import Dialog from 'components/dialog'

const useStyles = makeStyles(theme => ({
  titleRoot: {
    padding: theme.spacing(3, 7, 1, 3),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#172024',
  },
  subtitle: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
  },
  content: {
    padding: theme.spacing(1, 3, 2),
  },
  bodyText: {
    margin: 0,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  formStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
}))

const deleteCoachConnectionMutation = gql`
  mutation DeleteCoachConnection($studentId: ID!, $deletionReason: String) {
    deleteCoachConnection(
      studentId: $studentId
      deletionReason: $deletionReason
    ) {
      message
      success
    }
  }
`

const DeleteStudentDialog = ({ student, open, onSuccess, setOpen }) => {
  const classes = useStyles()
  const [deleteStudent] = useMutation(deleteCoachConnectionMutation)

  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)

  const [customReason, setCustomReason] = useState('')
  const [customReasonError, setCustomReasonError] = useState(false)
  const [deletionReason, setDeletionReason] = useState('')
  const [selectError, setSelectError] = useState(false)

  const MAX_CUSTOM_REASON_LENGTH = 255

  const DELETION_REASONS = [
    'Multiple accounts for this student',
    'Student is no longer in program',
    'Student moved away',
    'Other',
  ]

  const resetForm = () => {
    setCustomReason('')
    setCustomReasonError(false)
    setDeletionReason('')
    setSelectError(false)
  }

  const handleClose = () => {
    resetForm()
    setOpen(false)
  }

  const handleSelectChange = event => {
    if (selectError && event.target.value !== '') {
      setSelectError(false)
    }
    setDeletionReason(event.target.value)
  }

  const handleCustomChange = event => {
    setCustomReason(event.target.value)
    if (event.target.value.length > MAX_CUSTOM_REASON_LENGTH) {
      setCustomReasonError(true)
    } else {
      setCustomReasonError(false)
    }
  }

  const handleDeleteStudent = async () => {
    if (deletionReason === '') {
      setSelectError(true)
      return
    }
    if (customReasonError) {
      return
    }
    try {
      const { data } = await deleteStudent({
        variables: {
          studentId: student.id,
          deletionReason:
            deletionReason === 'Other' ? customReason : deletionReason,
        },
      })
      const deleted = get(data, ['deleteCoachConnection', 'success'])

      if (!deleted) {
        return snackbarError()
      }

      resetForm()
      setOpen(false)
      onSuccess()
      snackbarSuccess(`${student.firstName} successfully removed`)
    } catch (e) {
      snackbarError()
    }
  }

  const isDeleteButtonDisabled = () => {
    if (deletionReason === '') {
      return true
    }

    return (
      deletionReason === 'Other' &&
      (customReason == null || customReason === '')
    )
  }

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      showCloseButton
      fullWidth
      maxWidth="xs"
    >
      {() => (
        <>
          <DialogTitle disableTypography classes={{ root: classes.titleRoot }}>
            <Typography component="h2" className={classes.title}>
              Delete Student
            </Typography>
            <Typography className={classes.subtitle}>
              This student will be removed from your coach profile.
            </Typography>
          </DialogTitle>
          <DialogContent classes={{ root: classes.content }}>
            <Typography className={classes.bodyText}>
              Are you sure you want to delete this student? Please provide a
              reason to help us improve both the coach and golfer experience.
            </Typography>
            <Box className={classes.formStack}>
              <FormControl
                required
                variant="outlined"
                error={selectError}
                fullWidth
              >
                <InputLabel>Select Reasoning</InputLabel>
                <Select
                  value={deletionReason}
                  onChange={handleSelectChange}
                  label="Select Reasoning *"
                  inputProps={{ 'data-testid': 'deletion-reason-select' }}
                >
                  {DELETION_REASONS.map((reason, index) => (
                    <MenuItem key={index} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
                {selectError ? (
                  <FormHelperText error>Please select a reason.</FormHelperText>
                ) : null}
              </FormControl>
              {deletionReason === 'Other' ? (
                <TextField
                  label="Please Explain Below *"
                  multiline
                  maxRows={4}
                  value={customReason}
                  onChange={handleCustomChange}
                  error={customReasonError}
                  helperText={
                    customReasonError
                      ? `Reason must be less than ${MAX_CUSTOM_REASON_LENGTH} characters.`
                      : null
                  }
                  inputProps={{ 'data-testid': 'custom-reason-input' }}
                  variant="outlined"
                  fullWidth
                />
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button color="primary" size="large" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="large"
              disableElevation
              startIcon={<DeleteOutlineOutlined fontSize="small" />}
              onClick={handleDeleteStudent}
              disabled={
                selectError || customReasonError || isDeleteButtonDisabled()
              }
            >
              Delete Student
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default DeleteStudentDialog
